import { useParams } from "react-router-dom";

import { getPublicMention } from "@/api/endpoints/mentions";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import useSearchParams from "@/hooks/useSearchParams";
import Error from "@components/Error";
import MentionCardNew, {
  SkeletonMentionCard,
} from "@components/MentionCardNew";
import SharedLinkLayout from "@components/SharedLinkLayout";
import Typography from "@components/Typography";
import { useQuery } from "@tanstack/react-query";

import styles from "./PublicSingleMentionView.module.css";

const PublicSingleMentionView = () => {
  // URL state
  const { uuid, language } = useParams<{ uuid: string; language: string }>();
  const [{ kpis, sentiment }] = useSearchParams<{
    kpis: boolean;
    sentiment: boolean;
  }>();

  // Server state
  const { data, isLoading, error } = useQuery({
    queryKey: ["publicMention", uuid, language],
    queryFn: () => getPublicMention(uuid, language),
  });

  const { isVisible: isSkeletonVisible, shouldRender: showSkeleton } =
    useDelayedLoading(isLoading);

  // Helpers
  const hideKpis = kpis === false;
  const hideSentiment = sentiment === false;

  return (
    <SharedLinkLayout>
      <header className={styles.header}>
        <Typography className={styles.title} variant="h1">
          {data?.ok ? data.data.report_title : null}
        </Typography>
        <Typography className={styles.subtitle} variant="subtitle-1">
          {L.PUBLIC_MENTION_SUBTITLE}
        </Typography>
      </header>

      <div className="w-full max-w-(--site-content-max-width)">
        {data ? (
          data.ok ? (
            <MentionCardsProvider>
              <MentionCardNew
                mentionCard={data.data.mention_card}
                hideSentiment={hideSentiment}
                hidePrValue={hideKpis}
                hideReach={hideKpis}
                readOnly
              />
            </MentionCardsProvider>
          ) : (
            <Error />
          )
        ) : showSkeleton ? (
          <SkeletonMentionCard
            className={isSkeletonVisible ? "opacity-100" : "opacity-0"}
          />
        ) : error ? (
          <Error error={error.message} />
        ) : null}
      </div>
    </SharedLinkLayout>
  );
};

export default PublicSingleMentionView;
